<template>
    <div id="ControlTVATableWrapper" style="overflow-y: auto; max-height: 80vh">
        <!-- Control Group Table -->
        <div v-if="!getControlItems().length && !noDataReceived" class="d-flex my-4 justify-center">
            <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-if="getControlItems().length || noDataReceived" id="ControlTVATableContentWrapper">
            <!-- Control group Table -->
            <v-data-table
                :items="getControlItems()"
                :headers="getControlHeaders()"
                itemKey="vatRate"
                dense
                data-cy="control-table"
                hide-default-footer
            >
                <template #top>
                    <v-toolbar flat>
                        <v-toolbar-title>{{ $t("control-tva.control") }}</v-toolbar-title>
                    </v-toolbar>
                </template>
                <template #item.tax="{ item }"> {{ item.tax }} % </template>
                <template #item.theoricalCredit="{ item }">
                    {{ newCommonFormatCurrency(item.theoricalCredit) }}
                </template>
                <template #item.realCredit="{ item }">
                    {{ newCommonFormatCurrency(item.realCredit) }}
                </template>
                <template #item.gap="{ item }">
                    <span style="font-weight: bold"> {{ newCommonFormatCurrency(item.gap) }} </span>
                </template>
            </v-data-table>
            <!-- Product Account Groups Table -->
            <v-data-table
                :items="productAccountGroups"
                :headers="getProductHeaders()"
                itemKey="vatRate"
                class="mb-4"
                hide-default-footer
                dense
            >
                <template #top>
                    <v-toolbar flat>
                        <v-toolbar-title>{{ $t("control-tva.product-accounts") }}</v-toolbar-title>
                    </v-toolbar>
                </template>
                <template #item.vatRate="{ item }"> {{ item.vatRate }} % </template>
                <template #item.balance="{ item }">
                    {{ newCommonFormatCurrency(item.balance) }}
                </template>
                <template #item.accounts="{ item }">
                    <ul>
                        <li v-for="account in item.accounts" :key="account.name">
                            {{ account.name }} - {{ $t("control-tva.credit") }}
                            {{ newCommonFormatCurrency(account.balance) }}
                        </li>
                    </ul>
                </template>
                <template #item.tvaAmount="{ item }">
                    {{ newCommonFormatCurrency((item.balance * item.vatRate) / 100) }}
                </template>
            </v-data-table>
            <!-- Tva Account Groups Table -->
            <v-data-table
                :items="tvaAccountGroups"
                :headers="getTVAHeaders()"
                itemKey="vatRate"
                dense
                class="mb-4"
                hide-default-footer
            >
                <template #top>
                    <v-toolbar flat>
                        <v-toolbar-title>{{ $t("control-tva.tva-accounts") }}</v-toolbar-title>
                    </v-toolbar>
                </template>
                <template #item.vatRate="{ item }"> {{ item.vatRate }} % </template>
                <template #item.balance="{ item }">
                    {{ newCommonFormatCurrency(item.balance) }}
                </template>
                <template #item.accounts="{ item }">
                    <ul>
                        <li v-for="account in item.accounts" :key="account.name">
                            {{ account.name }} - {{ $t("control-tva.credit") }}
                            {{ newCommonFormatCurrency(account.balance) }}
                        </li>
                    </ul>
                </template>
            </v-data-table>
        </div>
    </div>
</template>
<script lang="ts">
import { Component, Prop, toNative, Vue } from "vue-facing-decorator";
import {
    ControlTableItemDto,
    VatControlQueryDto,
    ProductAccountGroupDto,
    VatControlDto,
} from "./ControlTVA.dto";
import { groupByVatRate } from "./ControlTVA.helper";
import { VDataTable } from "vuetify/lib/components/index.mjs";
import { Apollo } from "@/decorators";
import VAT_CONTROL from "../../graphql/declaration/VatControl.gql";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";

@Component({
    name: "ControlTVATable",
    components: {
        VDataTable,
    },
})
export class ControlTVATableComponent extends Vue {
    /**
     * Determines the date to calculate the VAT Control from the general ledger.
     */
    @Prop() inputDate?: string;

    // async beforeMount() {
    //     await this.analyseTvaAccounts();
    // }
    productAccountGroups: ProductAccountGroupDto[];
    tvaAccountGroups: ProductAccountGroupDto[];

    get companyId() {
        return this.$store?.state?.selected_company?.id || undefined;
    }

    newCommonFormatCurrency(amount: number, options = undefined) {
        return newCommonFormatCurrency(amount, options);
    }

    @Apollo({
        query: VAT_CONTROL,
        variables() {
            return {
                companyId: this.companyId,
                endDate: this.inputDate,
            };
        },
        skip() {
            return !this.companyId || !this.inputDate;
        },
        async specificResult({ data, loading, networkStatus }: VatControlQueryDto) {
            console.log(data);
            if (!loading) {
                if (data?.vatControl?.result) {
                    this.vatControl = data.vatControl.result;
                    await this.analyseTvaAccounts();
                    this.noDataReceived = false;
                } else {
                    this.noDataReceived = true;
                }
            }
        },
    })
    noDataReceived = false;
    vatControl: VatControlDto = undefined;

    /**
     * Analyses the TVA accounts and their counterparts ( this meaning, accounts that start with 70 and accounts that start with 445)
     * The code places each one of these twwo groups into a groupByVatRate function that returns an array of ProductAccountGroupDto
     * The array is then assigned to the productAccountGroups and tvaAccountGroups variables
     * The function then returns a Promise that resolves to void. When finished, the value of getControlItems() will be refreshed
     */
    async analyseTvaAccounts(): Promise<void> {
        const productAccounts =
            this.$store.state.accountableAccountTable.accountableAccounts.filter((account) => {
                return account.name.startsWith("70") && account.vatRate;
            });
        const tvaAccounts = this.$store.state.accountableAccountTable.accountableAccounts.filter(
            (account) => {
                return account.name.startsWith("445") && account.vatRate;
            },
        );
        this.productAccountGroups = Object.values(
            groupByVatRate(this.vatControl.balances70, productAccounts),
        );
        this.tvaAccountGroups = Object.values(
            groupByVatRate(this.vatControl.balances445, tvaAccounts),
        );
    }

    getProductHeaders() {
        return [
            { title: this.$t("control-tva.vat-rate"), value: "vatRate" },
            { title: this.$t("control-tva.total-credit"), value: "balance" },
            { title: this.$t("control-tva.accounts"), value: "accounts" },
            { title: this.$t("control-tva.montant-product"), value: "tvaAmount" },
        ];
    }

    getTVAHeaders() {
        return [
            { title: this.$t("control-tva.vat-rate"), value: "vatRate" },
            { title: this.$t("control-tva.montant-tva"), value: "balance" },
            { title: this.$t("control-tva.solde-hors-journal-tva"), value: "accounts" },
        ];
    }

    getControlHeaders() {
        return [
            { title: this.$t("control-tva.vat"), value: "tax" },
            { title: this.$t("control-tva.solde-theorique"), value: "theoricalCredit" },
            { title: this.$t("control-tva.solde-reel"), value: "realCredit" },
            { title: this.$t("control-tva.ecart"), value: "gap" },
        ];
    }

    /**
     * This function gets the control items by merging the product and tva account groups
     * @returns ControlTableItemDto[] thas is used in the final table.
     */
    getControlItems(): ControlTableItemDto[] {
        if (!this.productAccountGroups || !this.tvaAccountGroups) {
            return [];
        }
        const controlData: Record<number, ControlTableItemDto> = {};
        this.productAccountGroups.forEach((productAccountGroup) => {
            if (!controlData[productAccountGroup.vatRate]) {
                controlData[productAccountGroup.vatRate] = {
                    tax: productAccountGroup.vatRate,
                    theoricalCredit:
                        (productAccountGroup.balance * productAccountGroup.vatRate) / 100,
                    realCredit: 0,
                    gap: (productAccountGroup.balance * productAccountGroup.vatRate) / 100,
                };
            } else {
                controlData[productAccountGroup.vatRate].theoricalCredit +=
                    productAccountGroup.balance;
                controlData[productAccountGroup.vatRate].gap +=
                    (productAccountGroup.balance * productAccountGroup.vatRate) / 100;
            }
        });

        this.tvaAccountGroups.forEach((productAccountGroup) => {
            if (!controlData[productAccountGroup.vatRate]) {
                controlData[productAccountGroup.vatRate] = {
                    tax: productAccountGroup.vatRate,
                    theoricalCredit: 0,
                    realCredit: productAccountGroup.balance,
                    gap: -productAccountGroup.balance,
                };
            } else {
                controlData[productAccountGroup.vatRate].realCredit += productAccountGroup.balance;
                controlData[productAccountGroup.vatRate].gap -= productAccountGroup.balance;
            }
        });
        return Object.values(controlData);
    }
}
export default toNative(ControlTVATableComponent);
</script>
<style lang="scss" scoped></style>
